import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import { useYupFields } from 'hooks/forms/useYupFields'
import { createBeneficiaryNameSchema } from 'utils/yupSchemas/createGenericNameSchema'
import { createPaymentReferenceSchema } from 'utils/yupSchemas/createReferenceSchema'
import { isAddressFieldsRequired } from '../utils/fiatInstruments'
import {
  accountNumberConfirmValidation,
  accountNumberValidation,
  bankCodeValidation,
  validateAccountType,
  validateAddressField,
  validateIntermediaryAddressField,
  validateUsdRoutingNumber,
} from './fiatBankAccountValidators'

export type ContactFiatInstrumentFormData = {
  // Basic beneficiary details
  transferDestination: string
  entityType: string
  description: string
  currency: string
  paymentReference?: string
  isSelfOwned: boolean
  beneficiaryAddressLine1?: string
  beneficiaryAddressLine2?: string
  beneficiaryCity?: string
  beneficiaryRegion?: string
  beneficiaryPostCode?: string
  beneficiaryCountryCode?: string
  beneficiaryCountryName?: string
  beneficiaryBankName?: string
  // Bank account details
  accountNumber: string
  accountNumberConfirm?: string // USD ACH and FEDWIRE
  bankCode: string
  routingNumber?: string
  accountType?: string
  paymentMethod?: string
  bankName?: string
  bankAddressLine1?: string
  bankAddressLine2?: string
  bankCity?: string
  bankRegion?: string
  bankPostCode?: string
  bankCountryCode?: string
  bankCountryName?: string
  // Intermediary bank details
  intermediaryBankCode?: string
  intermediaryBankRoutingNumber?: string
  intermediaryBankName?: string
  intermediaryBankAddressLine1?: string
  intermediaryBankAddressLine2?: string
  intermediaryBankCity?: string
  intermediaryBankRegion?: string
  intermediaryBankPostCode?: string
  intermediaryBankCountryCode?: string
  // Auth
  twoFactorCode?: string
  challengeCode?: string
  callbackUrl?: string
}

export type ContactFiatInstrumentFormKeys = keyof ContactFiatInstrumentFormData

export const contactFiatInstrumentSchema = (t: TFunction<'translation', undefined>) => {
  const { getRequiredYupField, getCheckboxYupField, getConditionalYupField } = useYupFields()

  return yup.object<Record<ContactFiatInstrumentFormKeys, yup.AnySchema>>({
    // Basic beneficiary details
    transferDestination: getRequiredYupField({
      label: t('formFields.transferDestination'),
    }),
    entityType: getRequiredYupField({
      label: '',
    }),
    description: createBeneficiaryNameSchema(t('formFields.beneficiaryDescription'), t),
    currency: getRequiredYupField({
      label: t('formFields.currency'),
    }),
    paymentReference: createPaymentReferenceSchema(t('formFields.paymentReference'), t, false),
    isSelfOwned: getCheckboxYupField(t('formFields.selfOwnedBankAccount')),
    beneficiaryAddressLine1: yup
      .string()
      .label(t('addressLine1'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('addressLine1'))),
    beneficiaryAddressLine2: yup.string().optional(),
    beneficiaryCity: yup
      .string()
      .label(t('city'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('city'))),
    beneficiaryRegion: yup
      .string()
      .label(t('region'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('region'))),
    beneficiaryPostCode: yup
      .string()
      .label(t('postCode'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('postCode'))),
    beneficiaryCountryCode: yup
      .string()
      .label(t('country'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('country'))),
    beneficiaryCountryName: yup.string().optional(),
    beneficiaryBankName: yup.string().test({
      test(value, ctx) {
        const isRequired = isAddressFieldsRequired({
          currency: ctx?.parent?.currency,
          transferDestination: ctx?.parent?.transferDestination,
          paymentMethod: ctx?.parent?.paymentMethod,
        })

        if (!value && isRequired) {
          return ctx.createError({
            message: t?.('fieldIsARequiredField', {
              field: t('bankName'),
              interpolation: { escapeValue: false },
            }),
          })
        }

        return true
      },
    }),

    // Bank account details (same as before)
    accountNumber: yup.string().test((value, ctx) => accountNumberValidation(value, ctx, t)),
    accountNumberConfirm: yup
      .string()
      .test((value, ctx) => accountNumberConfirmValidation(value, ctx, t)),
    bankCode: yup.string().test((value, ctx) => bankCodeValidation(value, ctx, t)),
    routingNumber: yup.string().test((value, ctx) => validateUsdRoutingNumber({ value, ctx, t })),
    accountType: yup.string().test((value, ctx) => validateAccountType(value, ctx, t)),
    paymentMethod: getConditionalYupField({
      label: t('formFields.paymentMethod'),
      fieldKey: 'currency',
      fieldValue: 'USD',
    }),
    bankName: yup.string().notRequired(),
    bankCountryName: yup.string().notRequired(),
    bankAddressLine1: yup
      .string()
      .label(t('addressLine1'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('addressLine1'))),
    bankAddressLine2: yup.string().optional(),
    bankCity: yup
      .string()
      .label(t('city'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('city'))),
    bankRegion: yup
      .string()
      .label(t('region'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('region'))),
    bankPostCode: yup
      .string()
      .label(t('postCode'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('postCode'))),
    bankCountryCode: yup
      .string()
      .label(t('country'))
      .test((value, ctx) => validateAddressField(value, ctx, t, t('country'))),

    // Intermediary bank details (same as before)
    intermediaryBankCode: yup.string().notRequired(),
    intermediaryBankRoutingNumber: yup.string().notRequired(),
    intermediaryBankName: yup.string().notRequired(),
    intermediaryBankAddressLine1: yup
      .string()
      .label(t('addressLine1'))
      .test((value, ctx) => validateIntermediaryAddressField(value, ctx, t, t('addressLine1'))),
    intermediaryBankAddressLine2: yup.string().optional(),
    intermediaryBankCity: yup
      .string()
      .label(t('city'))
      .test((value, ctx) => validateIntermediaryAddressField(value, ctx, t, t('city'))),
    intermediaryBankRegion: yup
      .string()
      .label(t('region'))
      .test((value, ctx) => validateIntermediaryAddressField(value, ctx, t, t('region'))),
    intermediaryBankPostCode: yup
      .string()
      .label(t('postCode'))
      .test((value, ctx) => validateIntermediaryAddressField(value, ctx, t, t('postCode'))),
    intermediaryBankCountryCode: yup
      .string()
      .label(t('country'))
      .test((value, ctx) => validateIntermediaryAddressField(value, ctx, t, t('country'))),

    // Auth fields (same as before)
    twoFactorCode: yup.string().notRequired(),
    challengeCode: yup.string().notRequired(),
    callbackUrl: yup.string().notRequired(),
  })
}
